// Downloadable Chart Types
const DOWNLOADABLE_CHART_TYPES = ["line-chart", "pie-chart", "circular-progress-chart", "progress-chart", "waterfall-chart", "flow-chart", "ukraine-map"];
export function isChartDownloadable(chartType) {
  return DOWNLOADABLE_CHART_TYPES.includes(chartType);
} // CSV Helper Functions

function escapeCSVField(value) {
  // Convert null/undefined to empty string
  if (value === null || value === undefined) {
    return "";
  } // Convert to string


  const stringValue = String(value); // Check if field needs escaping (contains comma, quote, or newline)

  if (stringValue.includes(",") || stringValue.includes('"') || stringValue.includes("\n")) {
    // Escape quotes by doubling them
    const escapedValue = stringValue.replace(/"/g, '""'); // Wrap in quotes

    return `"${escapedValue}"`;
  }

  return stringValue;
}

function createCSVRow(values) {
  return values.map(escapeCSVField).join(",");
}

function createCSVFile(rows) {
  return rows.join("\n");
}

function sanitizeFilename(title) {
  // Replace unsafe characters with underscore
  let filename = title.replace(/[<>:"/\\|?*]/g, "_"); // Replace spaces with underscore

  filename = filename.replace(/ /g, "_"); // Collapse multiple underscores

  filename = filename.replace(/_+/g, "_"); // Trim underscores from ends

  filename = filename.replace(/^_+|_+$/g, ""); // Limit to 200 characters

  filename = filename.substring(0, 200);
  return filename;
}

function triggerDownload(content, filename) {
  // UTF-8 BOM bytes
  const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // Convert content string to UTF-8 bytes

  const encoder = new TextEncoder();
  const contentBytes = encoder.encode(content); // Combine BOM + content

  const finalBytes = new Uint8Array(bom.length + contentBytes.length);
  finalBytes.set(bom, 0);
  finalBytes.set(contentBytes, bom.length);
  const blob = new Blob([finalBytes], {
    type: "text/csv;charset=utf-8;"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click(); // Cleanup after 100ms

  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);
} // Chart Type Exporters


function exportLineChart(chart) {
  const sets = chart.data.sets; // Check if ANY set has delta/trend data

  let hasDelta = false;
  let hasTrend = false;

  for (const set of sets) {
    for (const point of set.points) {
      if (point.delta !== undefined) hasDelta = true;
      if (point.trend !== undefined) hasTrend = true;
    }
  } // Build headers


  const headers = ["Дата"];

  for (const set of sets) {
    headers.push(set.label);
    if (hasDelta) headers.push(`${set.label} (Δ)`);
    if (hasTrend) headers.push(`${set.label} (Тренд)`);
  } // Collect union of all dates across all sets


  const dateSet = new Set();

  for (const set of sets) {
    for (const point of set.points) {
      dateSet.add(point.x);
    }
  }

  const dates = Array.from(dateSet).sort(); // Build rows

  const rows = [createCSVRow(headers)];

  for (const date of dates) {
    const rowValues = [date];

    for (const set of sets) {
      // Find matching point for this date in this set
      const point = set.points.find(p => p.x === date);

      if (point) {
        rowValues.push(point.y);
        if (hasDelta) rowValues.push(point.delta || "");
        if (hasTrend) rowValues.push(point.trend || "");
      } else {
        // Empty cells for missing data
        rowValues.push("");
        if (hasDelta) rowValues.push("");
        if (hasTrend) rowValues.push("");
      }
    }

    rows.push(createCSVRow(rowValues));
  }

  return createCSVFile(rows);
}

function exportPieChart(chart) {
  const headers = ["Категорія", "Значення (%)"];
  const rows = [createCSVRow(headers)];

  for (const set of chart.data.sets) {
    rows.push(createCSVRow([set.label, set.value]));
  }

  return createCSVFile(rows);
}

function exportCircularProgressChart(chart) {
  const headers = ["Назва", "Значення (%)", "Деталі"];
  const rows = [createCSVRow(headers)];

  for (const set of chart.data.sets) {
    rows.push(createCSVRow([set.label, set.value, set.details || ""]));
  }

  return createCSVFile(rows);
}

function exportProgressChart(chart) {
  // Check if ANY set has label
  const hasLabels = chart.data.sets.some(set => set.label !== undefined); // Build headers

  const headers = [];
  if (hasLabels) headers.push("Назва");
  headers.push("Значення (%)");
  headers.push("Деталі");
  const rows = [createCSVRow(headers)];

  for (const set of chart.data.sets) {
    const rowValues = [];
    if (hasLabels) rowValues.push(set.label || "");
    rowValues.push(set.value);
    rowValues.push(set.details || "");
    rows.push(createCSVRow(rowValues));
  }

  return createCSVFile(rows);
}

function exportWaterfallChart(chart) {
  const headers = ["Дата", "Набір", "Колір набору", "Верх діапазону", "Верх значення", "Низ значення", "Низ діапазону"];
  const rows = [createCSVRow(headers)]; // Note: sets is an object, not an array

  for (const point of chart.data.points) {
    const set = chart.data.sets[point.set];
    rows.push(createCSVRow([point.x, set.label, set.color, point.bandTop, point.valueTop, point.valueBottom, point.bandBottom]));
  }

  return createCSVFile(rows);
}

function exportFlowChart(chart) {
  const rows = []; // Section 1: Nodes

  rows.push("=== Вузли ===");
  rows.push(createCSVRow(["ID", "Назва", "Значення", "Колонка", "Колір"]));

  for (const node of chart.data.nodes) {
    rows.push(createCSVRow([node.id, node.label, node.value, node.column, node.color]));
  } // Blank row separator


  rows.push(""); // Section 2: Links

  rows.push("=== Зв'язки ===");
  rows.push(createCSVRow(["Від", "До", "Потік"]));

  for (const link of chart.data.links) {
    rows.push(createCSVRow([link.from, link.to, link.flow]));
  }

  return createCSVFile(rows);
}

function exportUkraineMapChart(chart) {
  const headers = ["ID", "Назва", "Значення", "Форматоване значення", "Деталі"];
  const rows = [createCSVRow(headers)];

  for (const set of chart.data.sets) {
    rows.push(createCSVRow([set.id, set.label || "", set.value, set.valueFormatted || "", set.details || ""]));
  }

  return createCSVFile(rows);
} // Main Router Function


export function downloadChartAsCSV(chart, context = {}) {
  let csvContent;

  switch (chart.type) {
    case "line-chart":
      csvContent = exportLineChart(chart);
      break;

    case "pie-chart":
      csvContent = exportPieChart(chart);
      break;

    case "circular-progress-chart":
      csvContent = exportCircularProgressChart(chart);
      break;

    case "progress-chart":
      csvContent = exportProgressChart(chart);
      break;

    case "waterfall-chart":
      csvContent = exportWaterfallChart(chart);
      break;

    case "flow-chart":
      csvContent = exportFlowChart(chart);
      break;

    case "ukraine-map":
      csvContent = exportUkraineMapChart(chart);
      break;

    default:
      console.error(`Unknown chart type: ${chart.type}`);
      return;
  } // Build filename with date and context


  const filenameParts = [sanitizeFilename(chart.title)]; // Add current date

  const now = new Date();
  const dateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  filenameParts.push(dateStr); // Add section if available

  if (context.section) {
    filenameParts.push(sanitizeFilename(context.section));
  } // Add department filter if available and not empty


  if (context.department && context.department !== "") {
    filenameParts.push(sanitizeFilename(context.department));
  } // Add article filter if available


  if (context.article && context.article.length > 0) {
    const articleStr = context.article.join("_");
    filenameParts.push(sanitizeFilename(articleStr));
  } // Add period filter if available


  if (context.period && context.period !== "all") {
    filenameParts.push(context.period);
  }

  const filename = filenameParts.join("_") + ".csv";
  triggerDownload(csvContent, filename);
}